import { replace } from 'connected-react-router'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import {
  selectCreditCardStatisticsActiveBudgetCount,
  selectCreditCardStatisticsActiveVcnCount,
  selectCreditCardStatisticsBudgetCount,
  selectCreditCardStatisticsVcnCount,
} from 'src/app/Account/redux/account.selectors'
import {
  selectAccountResendVerificationError,
  selectAccountVerificationError,
  selectCreditCardIdFromRoute,
  selectPostingAccountVerification,
  selectPostingResendAccountVerification,
} from 'src/app/AccountOverview/redux/accountOverview.selectors'
import AccountOverviewBackgroundPageContent from 'src/components/AccountOverviewBackgroundPageContent/AccountOverviewBackgroundPageContent'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import { useBackgroundDispatch } from 'src/hooks'
import { useIsUserOrglessRCNOrBudgetOwner } from 'src/hooks/useOrglessUserStatus'
import { CreditCardStatus, CreditCardType } from 'src/models/creditCard.models'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import {
  accountBudgetsUrl,
  accountSettingsUrl,
  accountTransactionsUrl,
  accountVirtualCardsUrl,
  budgetOverviewUrl,
  transactionDetailUrl,
  virtualCardDetailUrl,
} from 'src/navigation/urls'
// Type only, so the legacy page and the redesigned tab describe tab availability
// with ONE type rather than two that can drift. Erased at compile time — no
// runtime dependency on the tab.
import type { AccountTabAvailability } from 'src/page-components/Accounts/AccountOverviewTab/AccountOverviewTab'
import {
  useQueryCreditCard,
  useQueryCreditCardPermissions,
  useQueryCreditCards,
} from 'src/queries/creditCard.queries'
import {
  useStartCreditCardVerification,
  useVerifyCreditCard,
} from 'src/queries/creditCards/creditCardWrites.queries'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { useQueryBudgetVirtualCardCounts } from 'src/queries/statistics/budgetVirtualCardCounts.queries'
import { useQueryVirtualCards } from 'src/queries/virtualCards.queries'
import { queryClient } from 'src/queryClient'
import { createSetIsSetupOrganizationPopupOpen } from 'src/redux/actions/app.actions'
import {
  getCreditCardPermissions,
  getParentCreditCard,
} from 'src/redux/actions/data/creditCards.actions'
import { selectIsConfirmRCNOrBudgetModalAccountOverviewDismissed } from 'src/redux/selectors/auth.selectors'

/**
 * Rows in each redesigned snapshot, matching the redesigned Overview tab's
 * `UPDATED_BUDGETS_SNAPSHOT_COUNT` / `VIRTUAL_CARDS_SNAPSHOT_COUNT` and the
 * design. Legacy asked for 3 and 8 — sized for a list of budget cards and a grid
 * of virtual-card tiles rather than for table rows — and those counts still
 * apply on the flag-off path.
 */
const UPDATED_SNAPSHOT_COUNT = 5
const LEGACY_BUDGETS_SNAPSHOT_COUNT = 3
const LEGACY_VIRTUAL_CARDS_SNAPSHOT_COUNT = 8

type AccountOverviewProps = {
  /**
   * Render the three redesigned tables (recent transactions, virtual cards,
   * budgets) in place of legacy's `BudgetsBox` and `VirtualCardsBox`.
   *
   * Computed by `AccountEntityPage` alongside every sibling gate and passed
   * down. Off, this page renders exactly what it rendered before.
   */
  showUpdatedCardOverviewTables?: boolean
  /**
   * Which sibling tabs this card actually has, from `AccountEntityPage` — the
   * same object the redesigned Overview tab is handed.
   *
   * Only read on the redesigned path, where each table links out to its tab.
   * Optional so the flag-off path, and any caller that renders this page without
   * the entity shell, keep working untouched.
   */
  tabAvailability?: AccountTabAvailability
}

const AccountOverview: React.FC<AccountOverviewProps> = ({
  showUpdatedCardOverviewTables = false,
  tabAvailability,
}) => {
  const dispatch = useBackgroundDispatch()

  const creditCardId = useSelector(selectCreditCardIdFromRoute)

  const {
    data: creditCard,
    isPending,
    isFetched: creditCardComplete,
    isError,
  } = useQueryCreditCard({
    creditCardId,
    enabled: !!creditCardId,
  })

  const { data: creditCardPermissions } = useQueryCreditCardPermissions({
    creditCardId,
    retry: false,
    enabled: !!creditCardId,
  })

  const history = useHistory()

  const activeVcnCount = useSelector(selectCreditCardStatisticsActiveVcnCount)
  const vcnCount = useSelector(selectCreditCardStatisticsVcnCount)
  const activeBudgetCount = useSelector(
    selectCreditCardStatisticsActiveBudgetCount,
  )
  const budgetCount = useSelector(selectCreditCardStatisticsBudgetCount)

  const postingAccountVerification = useSelector(
    selectPostingAccountVerification,
  )
  const accountVerificationError = useSelector(selectAccountVerificationError)
  const postingResendAccountVerification = useSelector(
    selectPostingResendAccountVerification,
  )
  const accountResendVerificationError = useSelector(
    selectAccountResendVerificationError,
  )

  const isUserOrglessRCNOrBudgetOwner = useIsUserOrglessRCNOrBudgetOwner()
  const isConfirmRCNOrBudgetModalDismissed = useSelector(
    selectIsConfirmRCNOrBudgetModalAccountOverviewDismissed,
  )
  // REDIRECT EFFECT
  // If the user somehow comes onto this page and this RCN does not haev virtual card features,
  // Redirect them to the account settings page
  useEffect(() => {
    if (creditCard?.status === CreditCardStatus.NotApplicable) {
      history.replace(accountSettingsUrl(creditCard.id))
    }
  }, [creditCard, history])

  const {
    data: budgetsData,
    isLoading: creditCardsLoading,
    isFetched: creditCardsComplete,
    isError: budgetsError,
  } = useQueryCreditCards({
    filters: {
      status: CreditCardStatus.Active,
      type: CreditCardType.Delegate,
      count: showUpdatedCardOverviewTables
        ? UPDATED_SNAPSHOT_COUNT
        : LEGACY_BUDGETS_SNAPSHOT_COUNT,
      page: 0,
      sortField: 'activeUpdatedAt',
      sortDirection: 'ASC',
      parentCreditCard: creditCardId,
    },
    // NOTE: If a credit card is physical only, we may want to disable this API
    // call for optimization. Holding off on this for now since it's not breaking anything
    // We can revisit this later
    enabled: !!creditCardId && !creditCard?.isBudget,
  })

  const {
    data: virtualCardsData,
    isLoading: virtualCardsLoading,
    isFetched: virtualCardsComplete,
    isError: virtualCardsError,
  } = useQueryVirtualCards({
    filters: {
      statuses: [VirtualCardStatus.Active],
      creditCardId,
    },
    perPage: showUpdatedCardOverviewTables
      ? UPDATED_SNAPSHOT_COUNT
      : LEGACY_VIRTUAL_CARDS_SNAPSHOT_COUNT,
    enabled: !!creditCardId,
  })

  // The Cards column of the redesigned budget table. Empty id list on the
  // flag-off path, which disables the query (see `budgetVirtualCardCountQueries`), so
  // legacy costs no extra request.
  const budgetIds = useMemo(
    () =>
      showUpdatedCardOverviewTables
        ? (budgetsData?.creditCards ?? []).map((budget) => budget.id)
        : [],
    [showUpdatedCardOverviewTables, budgetsData?.creditCards],
  )
  const budgetCardCounts = useQueryBudgetVirtualCardCounts(budgetIds)

  useEffect(() => {
    if (creditCard?.parentCreditCardId) {
      dispatch(getParentCreditCard(creditCard?.parentCreditCardId))
      dispatch(getCreditCardPermissions(creditCard?.parentCreditCardId))
    }
  }, [dispatch, creditCard?.parentCreditCardId])

  useEffect(() => {
    if (isUserOrglessRCNOrBudgetOwner && !isConfirmRCNOrBudgetModalDismissed) {
      dispatch(createSetIsSetupOrganizationPopupOpen(true))
    }
  }, [
    dispatch,
    isUserOrglessRCNOrBudgetOwner,
    isConfirmRCNOrBudgetModalDismissed,
  ])

  const verifyMutation = useVerifyCreditCard()
  const startVerificationMutation = useStartCreditCardVerification()

  const handleVerifyCreditCard = useCallback(
    (code: string): void => {
      verifyMutation.mutate({ creditCardId, body: { code } })
    },
    [verifyMutation, creditCardId],
  )

  const handleResendVerification = useCallback((): void => {
    startVerificationMutation.mutate({ creditCardId })
  }, [startVerificationMutation, creditCardId])

  // Navigation for the redesigned tables. They are routing-free by the
  // `src/feature/**` boundary, so the page owns every URL and every push and
  // hands them down; unused on the flag-off path, where the legacy boxes route
  // themselves.
  const handleNavigate = useCallback(
    (href: string): void => {
      history.push(href)
    },
    [history],
  )
  const handleSelectVirtualCard = useCallback(
    (virtualCardId: string): void => {
      history.push(virtualCardDetailUrl(virtualCardId))
    },
    [history],
  )
  const handleSelectBudget = useCallback(
    (budgetId: string): void => {
      history.push(budgetOverviewUrl(budgetId))
    },
    [history],
  )
  const handleSelectTransaction = useCallback(
    (transactionId: string, isSplit: boolean): void => {
      // Matches TransactionsDataGrid and the redesigned Overview tab: a split
      // transaction's cached detail is stale relative to the flyout about to
      // read it.
      if (isSplit) {
        queryClient.removeQueries({
          queryKey: transactionsQueryKeys.detail(transactionId),
        })
      }
      // The explicit empty search/hash/state is the location shape the
      // transaction flyout expects — pushing a bare string would let this route
      // inherit the current location's state.
      history.push({
        pathname: transactionDetailUrl(transactionId),
        search: '',
        hash: '',
        state: {},
      })
    },
    [history],
  )

  const location = useLocation()
  useEffect(() => {
    if (!creditCard?.isBudget) return
    const target = budgetOverviewUrl(creditCardId)
    if (location.pathname !== target) {
      dispatch(replace(target))
    }
  }, [dispatch, creditCard?.isBudget, creditCardId, location.pathname])

  const isLoading =
    isPending ||
    !creditCardComplete ||
    !creditCardsComplete ||
    !virtualCardsComplete

  if (isLoading) return <LoadingBackgroundPage />
  if (isError) return <NotFoundBackgroundPage />
  if (!creditCard) return <NotFoundBackgroundPage />

  return (
    <AccountOverviewBackgroundPageContent
      creditCard={creditCard}
      creditCardPermissions={creditCardPermissions}
      activeCreditCardBudgets={budgetsData?.creditCards || []}
      activeVcnCount={activeVcnCount}
      vcnCount={vcnCount}
      activeBudgetCount={activeBudgetCount}
      budgetCount={budgetCount}
      activeCreditCardVirtualCards={virtualCardsData?.virtualCards || []}
      virtualCardsLoading={virtualCardsLoading}
      virtualCardsComplete={virtualCardsComplete}
      creditCardsLoading={creditCardsLoading}
      creditCardsComplete={creditCardsComplete}
      // All-or-nothing: the gate and everything the tables need travel together,
      // so the body cannot be asked for a redesigned table without a
      // destination for its rows.
      updatedTables={
        showUpdatedCardOverviewTables
          ? {
              budgetCardCounts,
              // Without these a failed request reads as an empty one: both
              // queries report `isFetched` on error too, so a table would reach
              // its empty state and tell the user an account with cards has
              // none.
              budgetsError,
              virtualCardsError,
              onNavigate: handleNavigate,
              onSelectVirtualCard: handleSelectVirtualCard,
              onSelectBudget: handleSelectBudget,
              onSelectTransaction: handleSelectTransaction,
              virtualCardHref: virtualCardDetailUrl,
              budgetHref: budgetOverviewUrl,
              // Each dropped, not emptied, when the card has no such tab: a
              // link to a tab the subnav does not carry leaves the user
              // somewhere it cannot show as active. The subnav pushes all three
              // from this same `tabAvailability`, so one object decides both.
              //
              // The two conditions the LEGACY boxes used — `activeBudgetCount`
              // and `activeVcnCount` above zero — still decide whether each
              // TABLE renders, and are deliberately untouched. They are looser
              // than tab availability (budgets additionally needs `isActive`),
              // which is why a closed card could show a Budgets link with no
              // Budgets tab behind it. The table stays; only the link goes.
              accountBudgetsHref: tabAvailability?.budgets
                ? accountBudgetsUrl(creditCard.id)
                : undefined,
              accountVirtualCardsHref: tabAvailability?.virtualCards
                ? accountVirtualCardsUrl(creditCard.id)
                : undefined,
              accountTransactionsHref: tabAvailability?.transactions
                ? accountTransactionsUrl(creditCard.id)
                : undefined,
            }
          : undefined
      }
      postingAccountVerification={postingAccountVerification}
      accountVerificationError={accountVerificationError}
      postingResendAccountVerification={postingResendAccountVerification}
      accountResendVerificationError={accountResendVerificationError}
      handleVerifyCreditCard={handleVerifyCreditCard}
      handleResendVerification={handleResendVerification}
    />
  )
}

export default AccountOverview
